import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Loader2, Crown, Zap, CheckCircle2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { api, formatINR } from "../lib/api";

const PRICES = {
  pro: { monthly: 199, yearly: 1499 },
  elite: { monthly: 299, yearly: 2199 },
};
const NAMES = { pro: "ASCENDXP Pro", elite: "ASCENDXP Elite" };

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const plan = (params.get("plan") || "pro").toLowerCase();
  const cycle = (params.get("cycle") || "monthly").toLowerCase();
  const userIdParam = params.get("userId");
  const tokenParam = params.get("token");

  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [appFlow, setAppFlow] = useState(false);

  const price = PRICES[plan]?.[cycle] ?? 0;

  const setup = useCallback(async () => {
    try {
      let userId = userIdParam;
      let authToken = tokenParam;

      if (userId && authToken) {
        setAppFlow(true);
      } else {
        // Web visitor: create a guest session token so the flow works end-to-end.
        const guest = `web_${Math.random().toString(36).slice(2, 10)}`;
        const { data } = await api.post(`/checkout/dev-token?userId=${guest}`);
        userId = data.userId;
        authToken = data.authToken;
      }

      const { data } = await api.post("/checkout/session", {
        userId,
        selectedPlan: plan,
        billingCycle: cycle,
        authToken,
      });
      setSession({ ...data, userId, authToken });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to validate checkout session.");
    }
  }, [plan, cycle, userIdParam, tokenParam]);

  useEffect(() => {
    if (!PRICES[plan] || !PRICES[plan][cycle]) {
      setError("Invalid plan or billing cycle.");
      return;
    }
    setup();
  }, [setup, plan, cycle]);

  const handlePay = async () => {
    if (!session) return;
    setPaying(true);
    try {
      const { data: sub } = await api.post("/checkout/create-subscription", {
        userId: session.userId,
        selectedPlan: plan,
        billingCycle: cycle,
        authToken: session.authToken,
      });

      if (sub.razorpay_mock) {
        // MOCK FLOW: simulate gateway success then verify server-side.
        toast.info("Test mode: simulating Razorpay checkout…");
        const mockPaymentId = `pay_mock_${Math.random().toString(36).slice(2, 12)}`;
        const refId = sub.subscription_id || sub.order_id;
        const { data: sig } = await api.post(
          `/checkout/mock-signature?payment_id=${mockPaymentId}&ref_id=${refId}`
        );
        const { data: verify } = await api.post("/checkout/verify", {
          razorpay_payment_id: mockPaymentId,
          razorpay_subscription_id: sub.subscription_id,
          razorpay_order_id: sub.order_id,
          razorpay_signature: sig.signature,
          paymentRef: sub.paymentRef,
        });
        if (verify.success) onSuccess();
        return;
      }

      // REAL FLOW
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay.");
      const rzp = new window.Razorpay({
        key: sub.razorpay_key_id,
        subscription_id: sub.subscription_id,
        name: "ASCENDXP",
        description: `${NAMES[plan]} · ${cycle}`,
        theme: { color: "#4f46e5" },
        handler: async (resp) => {
          try {
            const { data: verify } = await api.post("/checkout/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
              paymentRef: sub.paymentRef,
            });
            if (verify.success) onSuccess();
            else navigate("/payment/failure");
          } catch {
            navigate("/payment/failure");
          }
        },
        modal: { ondismiss: () => navigate(`/payment/cancel?plan=${plan}&cycle=${cycle}`) },
      });
      rzp.on("payment.failed", () => navigate("/payment/failure"));
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Payment failed.");
      navigate("/payment/failure");
    } finally {
      setPaying(false);
    }
  };

  const onSuccess = () => {
    // Attempt deep link back to the app, then show the success page.
    try { window.location.href = "ascend://payment-success"; } catch {}
    navigate(`/payment/success?plan=${plan}`);
  };

  const Icon = plan === "elite" ? Crown : Zap;

  return (
    <div data-testid="checkout-page" className="pt-32 pb-24 min-h-screen relative">
      <Seo title="Secure Checkout — ASCENDXP" description="Complete your ASCENDXP premium upgrade securely." />
      <div className="absolute top-0 right-0 w-[500px] h-[400px] rounded-full bg-indigo-600/10 blur-[120px]" />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Secure Checkout</h1>
        <p className="text-zinc-500 mt-2 text-sm">Encrypted & verified server-side. Premium activates instantly.</p>

        {error ? (
          <div data-testid="checkout-error" className="mt-10 glass rounded-2xl p-8 border-red-500/30">
            <p className="text-red-400 font-semibold">{error}</p>
            <button onClick={() => navigate("/pricing")} className="btn-ghost mt-5">Back to Pricing</button>
          </div>
        ) : !session ? (
          <div className="mt-10 flex items-center gap-3 text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin" /> Validating session…
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            {/* Order summary */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass rounded-2xl p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-6">Order Summary</p>
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl grid place-items-center ${plan === "elite" ? "bg-[#d4af37]/15 text-[#d4af37]" : "bg-indigo-600/15 text-indigo-400"}`}>
                  <Icon className="w-7 h-7" />
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">{NAMES[plan]}</div>
                  <div className="text-sm text-zinc-500 capitalize">{cycle} billing · auto-renews</div>
                </div>
              </div>

              <div className="mt-8 space-y-3 text-sm border-t border-white/5 pt-6">
                <div className="flex justify-between text-zinc-400"><span>Plan</span><span className="text-white">{NAMES[plan]}</span></div>
                <div className="flex justify-between text-zinc-400"><span>Billing cycle</span><span className="text-white capitalize">{cycle}</span></div>
                <div className="flex justify-between text-zinc-400"><span>Taxes</span><span className="text-white">Included</span></div>
              </div>
              <div className="mt-6 flex justify-between items-end border-t border-white/5 pt-6">
                <span className="text-zinc-400">Total due today</span>
                <span className="font-heading font-black text-2xl text-gradient-gold">{formatINR(price)}</span>
              </div>
            </motion.div>

            {/* Payment panel */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass rounded-2xl p-8 flex flex-col">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-6">Payment</p>

              {appFlow ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400 mb-4">
                  <Smartphone className="w-4 h-4" /> Verified app session · user {session.userId}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-zinc-500 mb-4">
                  <ShieldCheck className="w-4 h-4" /> Guest web checkout session
                </div>
              )}

              {session.razorpay_mock && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs text-amber-300 mb-5">
                  <b>Test mode:</b> Razorpay keys not configured yet. Clicking pay simulates a
                  successful transaction so you can preview the full flow.
                </div>
              )}

              <ul className="space-y-3 text-sm text-zinc-300 mb-6">
                {["UPI · GPay · PhonePe · Paytm", "Credit & debit cards", "Net banking & wallets"].map((m) => (
                  <li key={m} className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-indigo-400" /> {m}</li>
                ))}
              </ul>

              <button
                data-testid="checkout-pay-btn"
                onClick={handlePay}
                disabled={paying}
                className="btn-primary w-full mt-auto disabled:opacity-60"
              >
                {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : <><Lock className="w-5 h-5" /> Pay {formatINR(price)}</>}
              </button>

              <p className="mt-4 text-[11px] text-zinc-600 text-center flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Secured by Razorpay · server-side verified
              </p>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
